import React from 'react';
import {
  FilterForm,
  SelectInput,
  TextInput,
  useGetList,
  useListContext,
} from 'react-admin';
import { Stack, Box } from '@mui/material';

import type { FacetGroup } from '@src/facets';

/**
 * Primary Name filter that depends on the selected Primary Tag filter.
 * Shows values from the selected tag's facet group.
 */
const PrimaryNameFilterInput = ({ alwaysOn }: { alwaysOn?: boolean }) => {
  const { data: facetGroups = [] } = useGetList<FacetGroup>('facets');
  const { filterValues, setFilters, displayedFilters } = useListContext();

  const selectedTag = filterValues?.tag as string | undefined;
  const tagFacet = facetGroups.find(fg => fg.tag === selectedTag);
  const nameChoices = tagFacet?.values.map(v => ({
    id: v.name,
    name: v.name,
  })) || [];

  return (
    <SelectInput
      source="name"
      label="Primary Name"
      choices={nameChoices}
      disabled={!selectedTag}
      resettable
      helperText={false}
      alwaysOn={alwaysOn}
      sx={{ width: '150px' }}
      onChange={(e) => {
        const newFilters = { ...filterValues, name: e?.target?.value || undefined };
        setFilters(newFilters, displayedFilters);
      }}
    />
  );
};

/**
 * Primary Tag filter that clears the Primary Name filter when changed.
 */
const PrimaryTagFilterInput = ({ choices, alwaysOn }: { choices: { id: string, name: string }[], alwaysOn?: boolean }) => {
  const { filterValues, setFilters, displayedFilters } = useListContext();

  return (
    <SelectInput
      source="tag"
      label="Primary Tag"
      choices={choices}
      resettable
      helperText={false}
      alwaysOn={alwaysOn}
      sx={{ width: '150px' }}
      onChange={(e) => {
        // Clear name when tag changes
        const newFilters = { ...filterValues, tag: e?.target?.value || undefined, name: undefined };
        setFilters(newFilters, displayedFilters);
      }}
    />
  );
};

const PricingRulesToolbar = () => {
  const { data: facetGroups = [], isLoading } = useGetList<FacetGroup>('facets');

  // Build choices for department and category filters
  const departmentFacet = facetGroups.find(fg => fg.tag === 'department');
  const categoryFacet = facetGroups.find(fg => fg.tag === 'category');

  const departmentChoices = departmentFacet?.values.map(v => ({
    id: v.name,
    name: v.name,
  })) || [];

  const categoryChoices = categoryFacet?.values.map(v => ({
    id: v.name,
    name: v.name,
  })) || [];

  // Build tag choices from all available facet tags
  const tagChoices = facetGroups.map(fg => ({
    id: fg.tag,
    name: fg.tag,
  }));

  const filters = [
    <SelectInput
      key="department"
      source="department"
      label="Department"
      choices={departmentChoices}
      disabled={isLoading}
      resettable
      helperText={false}
      alwaysOn
      sx={{ width: '150px' }}
    />,
    <SelectInput
      key="category"
      source="category"
      label="Category"
      choices={categoryChoices}
      disabled={isLoading}
      resettable
      helperText={false}
      alwaysOn
      sx={{ width: '150px' }}
    />,
    <PrimaryTagFilterInput key="tag" choices={tagChoices} alwaysOn />,
    // FIXME: Primary Name filter should clear when Primary Tag filter is cleared
    <PrimaryNameFilterInput key="name" alwaysOn />,
    <TextInput
      key="merchantCategory"
      source="merchantCategory"
      label="Merchant Category"
      resettable
      helperText={false}
      alwaysOn
      sx={{ width: '150px' }}
    />,
    <TextInput
      key="upc"
      source="upc"
      label="UPC"
      resettable
      helperText={false}
      alwaysOn
      sx={{ width: '150px' }}
    />,
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <FilterForm filters={filters} />
      </Stack>
    </Box>
  );
};

export default PricingRulesToolbar;
